import { DataStore, ItemReference, DataModel, Query, QueryResult } from 'src/core/stores/dataStore';
import { FormStore, FormModel } from 'src/core/stores/formStore';
import { repository, AsyncAction, reduce } from 'redux-scaffolding-ts';
import { container } from 'src/inversify.config';
import { Validator } from "lakmus";
import { CommandResult } from '../core/stores/types';
import { AxiosResponse } from 'axios';
import HttpService from '../core/services/http.service';

export interface ProjectMemberSummary {
    id: string;
    employeeId: string;
    employeeTitle: string;
    projectId: string;
    permission: number;
    projectTitle : string
}

export interface ProjectMemberItem extends NewProjectMemberItem {
    id: string;
}

export interface NewProjectMemberItem {
    employeeId: string[];
    projectId: string;
    permission: number;
}

export interface ProjectMemberBasicSummary {
    id:string
    employeeTitle: string
}

@repository("@@PROJECTMEMBERS", "projectMembers.summary")
export class ProjectMembersStore extends DataStore<ProjectMemberSummary> {
    baseUrl: string = ``;

    constructor() {
        super('PROJECTMEMBER', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }

    public changeUrlByProject(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects/${projectId}/members`;
    }

    public changeUrlByEmployee(employeeId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/timesheet/${employeeId}/members`;
    }
    public changeUrlByEmployeeAndCompany(employeeId: string, companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees/${employeeId}/projects`;
    }
    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<ProjectMemberSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectMemberSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }

    public async getProjects(query: Query, companyId: string) {
        let httpService = this.container.get<HttpService>(HttpService)
        const result = await httpService.get<any>(`${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects?${DataStore.buildUrl(query)}`)
        return result.data
    }
    public async assignProjects(companyId: string, employeeId:string, projectsId: string[]) {
        let httpService = this.container.get<HttpService>(HttpService)
        const result = await httpService.post(`${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees/${employeeId}/projects/assign-projects`, projectsId)
        return result.data
    }
    
}

@repository("@@PROJECTMEMBERS", "projectMembers.item")
export class ProjectMemberItemsStore extends DataStore<ProjectMemberSummary> {
    baseUrl: string = ``;

    constructor() {
        super('PROJECTMEMBERITEM', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }

    public changeUrlByProject(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects/${projectId}/members`;
    }

    public changeUrlByProjectEmployee(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects/ProjectEmployee`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<ProjectMemberSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectMemberSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }
}

export class NewProjectMemberValidator extends Validator<NewProjectMemberItem> {
    constructor() {
        super();

        this.ruleFor(x => x.employeeId)
            .notNull()
            .withMessage("A valid EmployeeId is required");

        this.ruleFor(x => x.projectId)
            .notNull()
            .withMessage("A valid ProjectId is required");
    }
}

export class ProjectMemberValidator extends Validator<ProjectMemberItem> {
    constructor() {
        super();
        
        this.ruleFor(x => x.employeeId)
            .notNull()
            .withMessage("A valid EmployeeId is required");

        this.ruleFor(x => x.projectId)
            .notNull()
            .withMessage("A valid ProjectId is required");
    }
}

const PROJECTMEMBER_UPDATE_ITEM = "PROJECTMEMBER_UPDATE_ITEM";
@repository("@@PROJECTMEMBERS", "projectMembers.detail")
export class ProjectMemberStore extends FormStore<ProjectMemberItem> {
    baseUrl: string = ``;

    protected validate(item: ProjectMemberItem) {
        return new ProjectMemberValidator().validate(item);
    }

    constructor() {
        super('PROJECTMEMBER', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
        this.addReducer(PROJECTMEMBER_UPDATE_ITEM, (): any => {
            return super.onPatch()
        }, 'AsyncAction');
    }

    public changeUrl(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects/${projectId}/members`;
    }

    public async getById(id: string, params?: any, companyId?: string): Promise<ProjectMemberItem> {
        var httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LOAD, httpService.get<ProjectMemberItem>(`${this.baseUrl}/${companyId}/${id}`, params));
        return result.data;
    }

    public async update(item: ProjectMemberItem) {
        var result = await super.patch(PROJECTMEMBER_UPDATE_ITEM, `${item.id}`, item) as any;
        return result.data as CommandResult<ProjectMemberItem>;
    }
}

@repository("@@PROJECTMEMBERS", "projectMembers.new")
export class NewProjectMemberStore extends FormStore<NewProjectMemberItem> {
    baseUrl: string = ``;

    protected validate(item: NewProjectMemberItem) {
        return (new NewProjectMemberValidator()).validate(item);
    }
    //protected validate(item: NewProjectMemberItem) {
    //    var currentValidation;
    //    var validator = new NewProjectMemberValidator();
    //    for (var i = 0; i < item.employeeId.length; i++) {
    //        currentValidation = validator.validate(item.employeeId[i]);
    //        if (!currentValidation.isValid) {
    //            return currentValidation;
    //        }
    //    }
    //    return currentValidation;
    //}

    constructor() {
        super('NEW_PROJECTMEMBER', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
    }

    public changeUrl(companyId: string, projectId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects/${projectId}/members`;
    }
}